import { Star, ExternalLink } from "lucide-react";

/**
 * Public-facing Google Business Reviews block.
 * Receives `data` from GET /api/business-reviews:
 *   { configured, place_url, rating, user_ratings_total, count, reviews, attribution }
 * Renders gracefully empty when data.configured is false or count is 0.
 */
function Stars({ value, size = "w-3 h-3" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`${size} ${s <= value ? "fill-[#9A7B4F] text-[#9A7B4F]" : "text-[#282828]/12"}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function GoogleAttribution() {
  return (
    <div
      className="flex items-center gap-1.5 text-[10px] text-[#676664]/40 tracking-wide"
      data-testid="google-attribution"
    >
      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
      </svg>
      <span>Powered by Google</span>
    </div>
  );
}

function BusinessReviewCard({ review }) {
  const date = review.publish_time
    ? new Date(review.publish_time).toLocaleDateString("en-AU", { month: "short", day: "numeric", year: "numeric" })
    : review.relative_time || "";
  const initial = (review.author_name || "G").trim().charAt(0).toUpperCase();
  return (
    <div className="py-4 border-b border-[#282828]/5 last:border-0" data-testid={`business-review-${review.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {review.author_photo ? (
            <img
              src={review.author_photo}
              alt={review.author_name}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#9A7B4F]/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[11px] text-[#9A7B4F] font-medium">{initial}</span>
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-[13px] text-[#282828] font-medium truncate">{review.author_name}</p>
              {review.featured && (
                <span className="text-[8px] text-[#9A7B4F] uppercase tracking-wider font-medium">· Featured</span>
              )}
            </div>
            <p className="text-[10px] text-[#676664]/40">{date}</p>
          </div>
        </div>
        <Stars value={review.rating || 0} />
      </div>
      {review.text && (
        <p className="text-[13px] text-[#5A5A5A] leading-relaxed mt-2.5 pl-[42px] whitespace-pre-line">
          {review.text}
        </p>
      )}
    </div>
  );
}

export default function BusinessReviewsList({ data }) {
  const reviews = (data && data.reviews) || [];
  const rating = (data && data.rating) || 0;
  const total = (data && data.user_ratings_total) || 0;
  const placeUrl = (data && data.place_url) || "";

  if (!reviews.length) {
    return (
      <div className="py-6" data-testid="business-reviews-empty">
        <p className="text-[13px] text-[#676664]/40">No Google reviews to display yet.</p>
        <div className="mt-3"><GoogleAttribution /></div>
      </div>
    );
  }

  return (
    <div data-testid="business-reviews-list">
      {/* Overall summary */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4 pb-4 border-b border-[#282828]/8">
        <div className="flex items-center gap-3">
          <div className="text-2xl text-[#282828] font-medium tabular-nums" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {rating ? rating.toFixed(1) : "—"}
          </div>
          <div>
            <Stars value={Math.round(rating)} size="w-3.5 h-3.5" />
            <p className="text-[11px] text-[#676664]/50 mt-0.5">
              Based on {total.toLocaleString()} Google review{total === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        <GoogleAttribution />
      </div>

      {/* Reviews */}
      <div>
        {reviews.map((r) => <BusinessReviewCard key={r.id} review={r} />)}
      </div>

      {/* View More on Google CTA */}
      {placeUrl && (
        <a
          href={placeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-5 text-[11px] tracking-[0.1em] uppercase text-[#9A7B4F] hover:text-[#8B6B3F] transition-colors"
          data-testid="view-more-on-google"
        >
          View more reviews on Google
          <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
        </a>
      )}
    </div>
  );
}
